import { Box, BoxProps, FormControl, HStack } from '@chakra-ui/react'
import { InputValueDataTypeConfig } from '@helo/cms'
import { Deferred } from '@helo/utils'
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { NumberInput } from '../../inputs/NumberInput'
import { SelectInput } from '../../inputs/SelectInput/SelectInput'
import { TextAreaInput } from '../../inputs/TextAreaInput'
import { TextInput } from '../../inputs/TextInput/TextInput'
import {
  ConditionOption,
  FilterEditorFormMethods,
} from '../StringFilterEditor/StringFilterEditor'

export type NumberFilterEditorProps = BoxProps & {
  inputValueConfig: InputValueDataTypeConfig
  conditionOptions: ConditionOption[]
  selectedField?: ConditionOption | undefined
  updateSelectedField?: (field: ConditionOption | undefined) => void
  selectedFieldRef?: React.MutableRefObject<ConditionOption | undefined>
  formMethods?: FilterEditorFormMethods
  onUpdate?: (config: InputValueDataTypeConfig) => void
  autoFocus?: boolean
  isDisabled?: boolean
  /** Called after a condition is selected, so parent can advance focus to the value input */
  onConditionSelected?: () => void
  /** When set, condition dropdown menu gets this data-click-boundary so parent popover doesn't close. */
  clickBoundaryId?: string
  /** When true, Enter with condition menu open stops propagation so parent modal does not also handle Enter. */
  stopEnterPropagationWhenMenuOpen?: boolean
  /** When false, condition menu is closed (e.g. parent popover closed) so portaled menus do not capture clicks. */
  isFilterEditorOpen?: boolean
}

export type NumberFilterEditorFields = {
  filterValue: string
}

type ConditionOption = {
  label: string
  value: string
  isArray?: boolean
  requiresValue?: boolean
}

const NUMBER_FILTER_MIN = -999999
const NUMBER_FILTER_MAX = 999999
const NUMBER_FILTER_MAX_DIGITS = 6

const clampNumber = (n: number): number =>
  Math.min(NUMBER_FILTER_MAX, Math.max(NUMBER_FILTER_MIN, n))

/** Enforce at most 6 digit characters; block further input (no auto-change to 999999). */
const limitToSixDigits = (value: string): string => {
  if (value === '' || value === '-') return value
  const hasMinus = value.startsWith('-')
  const digits = value.replace(/[^0-9]/g, '')
  if (digits.length <= NUMBER_FILTER_MAX_DIGITS) return value
  const truncated = digits.slice(0, NUMBER_FILTER_MAX_DIGITS)
  return hasMinus ? `-${truncated}` : truncated
}

export const NumberFilterEditor = ({
  inputValueConfig,
  conditionOptions,
  selectedField: selectedFieldProp,
  updateSelectedField: updateSelectedFieldProp,
  selectedFieldRef: selectedFieldRefProp,
  formMethods,
  onUpdate = () => {},
  autoFocus,
  isDisabled = false,
  onConditionSelected,
  clickBoundaryId,
  stopEnterPropagationWhenMenuOpen,
  isFilterEditorOpen = true,
  ...props
}: NumberFilterEditorProps) => {
  if (!formMethods) {
    return null
  }

  const { register, handleSubmit, setValue } = formMethods
  // Use props if provided (from FilterEditorRow), otherwise manage locally
  const [localSelectedField, setLocalSelectedField] = useState<
    ConditionOption | undefined
  >(undefined)
  const localSelectedFieldRef = useRef<ConditionOption | undefined>(undefined)
  // Preserve the last non-empty value so it can be restored when switching from no-value conditions (blank/notBlank)
  const preservedValueRef = useRef<string>('')

  const selectedField = selectedFieldProp ?? localSelectedField
  const selectedFieldRef = selectedFieldRefProp ?? localSelectedFieldRef
  const updateSelectedField =
    updateSelectedFieldProp ??
    ((field: ConditionOption | undefined) => {
      setLocalSelectedField(field)
      localSelectedFieldRef.current = field
    })

  const currentValue = inputValueConfig.state.asValue as
    | Record<string, any>
    | undefined

  const formatValueForDisplay = (val: unknown): string => {
    if (Array.isArray(val)) return val.join(', ')
    return val !== undefined && val !== null ? String(val) : ''
  }

  // Get the current filter value based on selected field
  const getCurrentFilterValue = (): string => {
    if (!currentValue || !selectedField) return ''

    const key = selectedField.value
    if (key === 'blank' || key === 'notBlank') return ''

    if (key === 'inRange') {
      const gte = currentValue.gte
      const lte = currentValue.lte
      if (typeof gte === 'number' && typeof lte === 'number') {
        return `${gte}, ${lte}`
      }
      if (typeof gte === 'number') return String(gte)
      if (typeof lte === 'number') return String(lte)
      return ''
    }

    if (key === 'notEqual') {
      if (currentValue.not?.equals !== undefined) {
        if (currentValue.not.equals === null) return ''
        return formatValueForDisplay(currentValue.not.equals)
      }
      return ''
    }

    if (currentValue[key] !== undefined) {
      const val = currentValue[key]
      return formatValueForDisplay(val)
    }

    if (key === 'equals' && currentValue.not?.equals !== undefined) {
      if (currentValue.not.equals === null) return ''
      return formatValueForDisplay(currentValue.not.equals)
    }

    return ''
  }

  // Get value for current condition, or preserve any existing number when switching conditions (avoid resetting to 0)
  const getCurrentFilterValueWithInitial = (): string => {
    const baseValue = getCurrentFilterValue()
    if (baseValue) return baseValue

    if (!currentValue || typeof currentValue !== 'object') return ''

    // inRange: preserve gte,lte
    if (
      typeof currentValue.gte === 'number' &&
      typeof currentValue.lte === 'number'
    ) {
      return `${currentValue.gte}, ${currentValue.lte}`
    }

    // When switching conditions (e.g. equals -> gt), preserve existing number from any condition key (skip 0 so input starts blank)
    const conditionKeys = conditionOptions.map(opt => opt.value)
    for (const key of conditionKeys) {
      const val = currentValue[key]
      if (typeof val === 'number' && !isNaN(val) && val !== 0) {
        return String(val)
      }
      if (
        Array.isArray(val) &&
        val.some((v: unknown) => typeof v === 'number')
      ) {
        return (val as number[]).filter(n => typeof n === 'number').join(', ')
      }
    }
    if (
      currentValue.not?.equals !== undefined &&
      typeof currentValue.not.equals === 'number' &&
      currentValue.not.equals !== 0
    ) {
      return String(currentValue.not.equals)
    }

    return ''
  }

  const { ref: formRef, ...filterValueRegistration } = register('filterValue')

  // Sync form value from filter state before paint to avoid placeholder flicker when changing condition
  useLayoutEffect(() => {
    const value = getCurrentFilterValueWithInitial()
    setValue('filterValue', value)
  }, [
    selectedField?.value,
    inputValueConfig.state.asValue,
    setValue,
    conditionOptions,
  ])

  const buildUpdatedConfigFromValue = (filterValue: string) => {
    const currentSelectedField = selectedFieldRef.current
    if (!currentSelectedField) return

    const newValue: Record<string, any> = {}
    const key = currentSelectedField.value

    if (key === 'blank') {
      newValue.equals = null
    } else if (key === 'notBlank') {
      newValue.not = { equals: null }
    } else if (key === 'notEqual') {
      if (filterValue.trim() !== '') {
        const numValue = Number(filterValue)
        if (!isNaN(numValue)) {
          newValue.not = { equals: clampNumber(numValue) }
        }
      }
    } else if (key === 'inRange') {
      const parts = filterValue.split(',').map(s => s.trim())
      const min = parts[0] !== '' ? Number(parts[0]) : undefined
      const max = parts[1] !== '' ? Number(parts[1]) : undefined
      if (min !== undefined && !isNaN(min)) newValue.gte = clampNumber(min)
      if (max !== undefined && !isNaN(max)) newValue.lte = clampNumber(max)
    } else if (key === 'equals') {
      if (!filterValue || filterValue.trim() === '') {
        // No value entered — don't store anything
      } else {
        const numValue = Number(filterValue)
        if (!isNaN(numValue)) {
          newValue.equals = clampNumber(numValue)
        }
      }
    } else if (currentSelectedField.isArray) {
      const arrayValues = filterValue
        .split(',')
        .map(item => {
          const trimmed = item.trim()
          if (trimmed === '') return undefined
          const num = Number(trimmed)
          return isNaN(num) ? undefined : clampNumber(num)
        })
        .filter(val => val !== undefined)
      if (arrayValues.length > 0) {
        newValue[key] = arrayValues
      }
    } else {
      if (filterValue.trim() !== '') {
        const numValue = Number(filterValue)
        if (!isNaN(numValue)) {
          newValue[key] = clampNumber(numValue)
        }
      }
    }

    return {
      ...inputValueConfig,
      state: Deferred.merge({ asValue: newValue })(
        inputValueConfig.state ?? {},
      ),
    }
  }

  const onSubmit = ({ filterValue }: NumberFilterEditorFields) => {
    const updatedConfig = buildUpdatedConfigFromValue(filterValue ?? '')
    if (updatedConfig) onUpdate(updatedConfig)
  }

  const handleValueChange = (filterValue: string) => {
    const updatedConfig = buildUpdatedConfigFromValue(filterValue)
    if (updatedConfig) onUpdate(updatedConfig)
  }

  const isMultiValue = selectedField?.isArray || false
  const showValueInput =
    selectedField != null && selectedField.requiresValue !== false
  const isNoValueCondition = selectedField?.requiresValue === false // Is Blank, Is Not Blank
  const isInRange = selectedField?.value === 'inRange'

  const [rangeFrom, setRangeFrom] = useState<string>(() => {
    if (!currentValue || typeof currentValue.gte !== 'number') return ''
    return String(currentValue.gte)
  })
  const [rangeTo, setRangeTo] = useState<string>(() => {
    if (!currentValue || typeof currentValue.lte !== 'number') return ''
    return String(currentValue.lte)
  })
  // Refs stay in sync so From/To updates in the same tick (common in Playwright)
  // do not drop the other bound via a stale React state closure.
  const rangeFromRef = useRef(rangeFrom)
  const rangeToRef = useRef(rangeTo)

  // Only sync range values from the config when switching TO inRange condition,
  // not on every currentValue change (which would overwrite user input mid-typing).
  const prevIsInRange = useRef(isInRange)
  useLayoutEffect(() => {
    if (isInRange && !prevIsInRange.current) {
      const gte = currentValue?.gte
      const lte = currentValue?.lte
      const fromStr = typeof gte === 'number' ? String(gte) : ''
      const toStr = typeof lte === 'number' ? String(lte) : ''
      // Use preserved value for "from" when transitioning to inRange (e.g. from single-value or blank)
      const preservedParts = preservedValueRef.current
        .split(',')
        .map(s => s.trim())
      const nextFrom = fromStr || preservedParts[0] || ''
      const nextTo = toStr || preservedParts[1] || ''
      rangeFromRef.current = nextFrom
      rangeToRef.current = nextTo
      setRangeFrom(nextFrom)
      setRangeTo(nextTo)
    }
    prevIsInRange.current = isInRange
  }, [isInRange, currentValue?.gte, currentValue?.lte])

  const handleRangeChange = (which: 'from' | 'to', value: string) => {
    const limited = limitToSixDigits(value)
    const nextFrom = which === 'from' ? limited : rangeFromRef.current
    const nextTo = which === 'to' ? limited : rangeToRef.current
    rangeFromRef.current = nextFrom
    rangeToRef.current = nextTo
    if (which === 'from') {
      setRangeFrom(limited)
    } else {
      setRangeTo(limited)
    }
    const fromNum = nextFrom.trim() === '' ? undefined : Number(nextFrom)
    const toNum = nextTo.trim() === '' ? undefined : Number(nextTo)
    const newValue: Record<string, any> = {}
    if (fromNum !== undefined && !isNaN(fromNum))
      newValue.gte = clampNumber(fromNum)
    if (toNum !== undefined && !isNaN(toNum)) newValue.lte = clampNumber(toNum)
    onUpdate({
      ...inputValueConfig,
      state: Deferred.merge({ asValue: newValue })(
        inputValueConfig.state ?? {},
      ),
    })
  }

  return (
    <>
      <Box data-testid="number-filter-condition-select" flex="1" minWidth={0}>
        <SelectInput
          variant="filterCondition"
          options={conditionOptions}
          placeholder="Search or select condition"
          isDisabled={isDisabled}
          filterMode="substring"
          menuBoundaryId={clickBoundaryId}
          stopEnterPropagationWhenMenuOpen={stopEnterPropagationWhenMenuOpen}
          menuIsOpen={isFilterEditorOpen ? undefined : false}
          onChange={(option: any) => {
            const selected = conditionOptions.find(
              opt => opt.value === option.value,
            )
            if (selected) {
              // Preserve the current value before switching (only from value-requiring conditions)
              if (selectedField?.requiresValue !== false) {
                if (selectedField?.value === 'inRange') {
                  // Save range as "from, to" so both parts can be restored
                  const from = rangeFrom.trim()
                  const to = rangeTo.trim()
                  if (from || to) {
                    preservedValueRef.current = to ? `${from}, ${to}` : from
                  }
                } else {
                  const currentTrimmed =
                    getCurrentFilterValueWithInitial().trim()
                  if (currentTrimmed) {
                    // Keep the "to" part from a previously preserved range so inRange can restore both values
                    const existingParts = preservedValueRef.current.split(',')
                    const existingTo = existingParts[1]?.trim()
                    preservedValueRef.current = existingTo
                      ? `${currentTrimmed}, ${existingTo}`
                      : currentTrimmed
                  }
                }
              }
              updateSelectedField(selected)
              onConditionSelected?.()
              // Don't clear the field value - keep it when changing condition
              queueMicrotask(() => {
                const currentFormValue = getCurrentFilterValueWithInitial()
                // Fall back to preserved value when empty (e.g. after blank/notBlank)
                const trimmedValue =
                  currentFormValue.trim() || preservedValueRef.current
                // For single-value conditions, extract just the first value from a preserved range (e.g. "10, 100" → "10")
                const singleValue = trimmedValue.includes(',')
                  ? trimmedValue.split(',')[0].trim()
                  : trimmedValue
                const newValue: Record<string, any> = {}

                if (selected.value === 'blank') {
                  newValue.equals = null
                } else if (selected.value === 'notBlank') {
                  newValue.not = { equals: null }
                } else if (selected.value === 'notEqual') {
                  if (singleValue !== '') {
                    const numValue = Number(singleValue)
                    if (!isNaN(numValue)) {
                      newValue.not = { equals: numValue }
                    }
                  }
                } else if (selected.value === 'inRange') {
                  const parts = trimmedValue.split(',').map(s => s.trim())
                  const min = parts[0] !== '' ? Number(parts[0]) : undefined
                  const max = parts[1] !== '' ? Number(parts[1]) : undefined
                  if (min !== undefined && !isNaN(min)) newValue.gte = min
                  if (max !== undefined && !isNaN(max)) newValue.lte = max
                } else if (selected.value === 'equals') {
                  if (singleValue === '') {
                    // No value entered — don't store anything
                  } else {
                    const numValue = Number(singleValue)
                    if (!isNaN(numValue)) {
                      newValue.equals = numValue
                    }
                  }
                } else if (selected.isArray) {
                  const arrayValues = trimmedValue
                    .split(',')
                    .map(item => {
                      const t = item.trim()
                      if (t === '') return undefined
                      const num = Number(t)
                      return isNaN(num) ? undefined : num
                    })
                    .filter(val => val !== undefined)
                  if (arrayValues.length > 0) {
                    newValue[selected.value] = arrayValues
                  }
                } else {
                  if (singleValue !== '') {
                    const numValue = Number(singleValue)
                    if (!isNaN(numValue)) {
                      newValue[selected.value] = numValue
                    }
                  }
                }

                const updatedConfig = {
                  ...inputValueConfig,
                  state: Deferred.merge({ asValue: newValue })(
                    inputValueConfig.state ?? {},
                  ),
                }
                onUpdate(updatedConfig)
              })
            }
          }}
          value={selectedField}
          flex="1"
        />
      </Box>
      {conditionOptions.length > 0 &&
        (showValueInput || isNoValueCondition) && (
          <>
            {isNoValueCondition ? (
              <FormControl flex="1" isDisabled={isDisabled}>
                <TextInput placeholder="No value needed" disabled flex="1" />
              </FormControl>
            ) : isInRange ? (
              <HStack flex="1" gap="8px" alignItems="flex-end" minWidth={0}>
                <FormControl flex="1" minWidth={0} isDisabled={isDisabled}>
                  <NumberInput
                    value={rangeFrom}
                    maxLength={7}
                    onChange={e => {
                      const val = (e.target as HTMLInputElement).value ?? ''
                      handleRangeChange('from', val)
                    }}
                    placeholder="From"
                    isDisabled={isDisabled}
                    autoFocus={autoFocus}
                    hideStepper
                  />
                </FormControl>
                <FormControl flex="1" minWidth={0} isDisabled={isDisabled}>
                  <NumberInput
                    value={rangeTo}
                    maxLength={7}
                    onChange={e => {
                      const val = (e.target as HTMLInputElement).value ?? ''
                      handleRangeChange('to', val)
                    }}
                    placeholder="To"
                    isDisabled={isDisabled}
                    hideStepper
                  />
                </FormControl>
              </HStack>
            ) : isMultiValue ? (
              <FormControl flex="1" isDisabled={isDisabled}>
                <TextAreaInput
                  ref={formRef}
                  {...filterValueRegistration}
                  disabled={isDisabled}
                  onChange={e => {
                    filterValueRegistration.onChange(e)
                  }}
                  onBlur={() => {
                    handleSubmit(onSubmit)()
                  }}
                  onKeyUp={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      setValue('filterValue', e.currentTarget.value.trim())
                      handleSubmit(onSubmit)()
                    }
                  }}
                  autoFocus={autoFocus}
                  placeholder="Enter Value1, Value2, Value3"
                />
              </FormControl>
            ) : (
              <FormControl flex="1" isDisabled={isDisabled}>
                <NumberInput
                  data-testid="number-filter-value-input"
                  ref={formRef}
                  {...filterValueRegistration}
                  autoFocus={autoFocus}
                  placeholder="Enter value"
                  isDisabled={isDisabled}
                  hideStepper
                  maxLength={7}
                  onChange={e => {
                    const raw = String(
                      (e.target as HTMLInputElement).value ?? '',
                    )
                    const limited = limitToSixDigits(raw)
                    if (limited !== raw) {
                      ;(e.target as HTMLInputElement).value = limited
                      setValue('filterValue', limited)
                      handleValueChange(limited)
                      return
                    }
                    filterValueRegistration.onChange(e)
                    handleValueChange(raw)
                  }}
                  onBlur={() => handleSubmit(onSubmit)()}
                />
              </FormControl>
            )}
          </>
        )}
    </>
  )
}

NumberFilterEditor.displayName = 'NumberFilterEditor'
